// Klub / Jídelníček (26.8.2026).
//
// Klub nemá jídelníček na míru, má SDÍLENÉ KALORICKÉ ÚROVNĚ. Členka si
// v aplikaci vybere úroveň a vidí jídelníček té úrovně; na míru se nic
// negeneruje, protože 275 Kč měsíčně nemůže platit individuální práci.
//
// PROČ TAHLE STRÁNKA VYPADÁ JAKO KLIENTKA: každá úroveň je vedená jako
// "klient" s identitou "klub-jidelnicek-<kcal>" a stálým dotazníkem. Díky
// tomu se použije TENTÝŽ generátor, korektor češtiny i hlídač zakázaných
// surovin jako u jídelníčku na míru — včetně editoru, konceptů a zveřejnění.
// Kdyby se pro klub udělal druhý editor, každá budoucí oprava by se musela
// dělat dvakrát.
//
// Zveřejněná varianta úrovně je to, co členky uvidí v klubu.
import React, { useEffect, useState } from "react";
import { ChefHat, ChevronLeft, ChevronRight, Check, Pencil, Copy, Loader2 } from "lucide-react";
import { apiFetch } from "../../lib/api.js";
import { toast } from "../../components/Toast.jsx";
import GradientHeader from "../../components/GradientHeader.jsx";
import { Stranka } from "../../components/ui.jsx";
import { KartaPrehledu, HlavickaDlazdice } from "../../components/PrehledDlazdice.jsx";
import JidelnicekSection from "../../components/JidelnicekSection.jsx";

// Zrcadlí KCAL_UROVNE v netlify/functions/_lib/klubJidelnicekLib.cjs.
// Serverová knihovna je .cjs uvnitř funkcí, frontend si ji naimportovat
// nemůže; když se seznam úrovní změní, změň ho na OBOU místech.
const UROVNE = [1200, 1400, 1600, 1800, 2000, 2200];

// Jídelníček se generuje NA MĚSÍC, aby šlo stavět na sezónních surovinách
// (klubSezonaLib.cjs). Identita úrovně je proto "klub-jidelnicek-<kcal>-<měsíc>";
// každý měsíc má vlastní dotazník i vlastní varianty, takže se lednový
// jídelníček nepřepíše zářijovým.
const MESICE_NAZVY = ["leden", "únor", "březen", "duben", "květen", "červen",
  "červenec", "srpen", "září", "říjen", "listopad", "prosinec"];

const mesicIso = (posun) => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + posun);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const nazevMesice = (iso) => `${MESICE_NAZVY[Number(iso.slice(5, 7)) - 1]} ${iso.slice(0, 4)}`;

// VÝBĚR MĚSÍCE: rok se přepíná šipkami, měsíce jsou mřížka dvanácti zkratek.
//
// Seznam dlaždic na šest měsíců by po pár letech byl nekonečný pás a
// nativní pole typu month zase vykreslí modré okno prohlížeče, které
// s adminem nemá nic společného. Tohle se vejde na jeden řádek roku plus
// mřížku, vypadá to jako zbytek adminu a funguje to i na telefonu
// (Láďa, 28.8.2026).
const ZKRATKY = ["led", "úno", "bře", "dub", "kvě", "čvn", "čvc", "srp", "zář", "říj", "lis", "pro"];

// Barvy stavu: co je zveřejněné, co je rozdělané a co ještě není.
const STAV_STYL = {
  zverejneno: "border-emerald-300 bg-emerald-50 text-emerald-800",
  navrh: "border-amber-300 bg-amber-50 text-amber-800",
  nic: "border-slate-200 bg-slate-50 text-slate-500",
};
const STAV_POPIS = { zverejneno: "zveřejněno", navrh: "rozdělané", nic: "zatím nic" };

// Přepínač úrovně: zaoblený jako pilulky jinde v aplikaci, dotyková výška 44 px.
// Barva nese stav úrovně, vybraná je značková.
const pill = "inline-flex min-h-[44px] items-center gap-1.5 rounded-full border px-4 text-sm font-semibold transition-colors sm:min-h-0 sm:py-2";
const pillOn = "border-brand bg-brand text-white";

// Stejný výpočet jako v klubJidelnicekLib: bílkoviny 1,4 g/kg (0,064 g/kcal),
// tuky 30 % energie, zbytek sacharidy. Tady jen pro popisek nad editorem.
const na5 = (x) => Math.round(x / 5) * 5;
function makra(kcal) {
  const bilkoviny = na5(kcal * 0.064);
  const tuky = na5((kcal * 0.30) / 9);
  return { bilkoviny, tuky, sacharidy: na5((kcal - 4 * bilkoviny - 9 * tuky) / 4) };
}

function VyberMesice({ hodnota, onZmena }) {
  const [rok, setRok] = useState(() => Number(hodnota.slice(0, 4)));
  const dnesniMesic = mesicIso(0);

  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <button type="button" onClick={() => setRok((r) => r - 1)} aria-label="Předchozí rok"
          className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-xl border border-slate-200 bg-white p-1.5 text-slate-600 hover:bg-slate-50 sm:min-h-0 sm:min-w-0">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="min-w-[52px] text-center text-sm font-bold text-slate-800 tabular-nums">{rok}</span>
        <button type="button" onClick={() => setRok((r) => r + 1)} aria-label="Následující rok"
          className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-xl border border-slate-200 bg-white p-1.5 text-slate-600 hover:bg-slate-50 sm:min-h-0 sm:min-w-0">
          <ChevronRight className="h-4 w-4" />
        </button>
        <button type="button" onClick={() => { setRok(Number(dnesniMesic.slice(0, 4))); onZmena(dnesniMesic); }}
          className="ml-1 min-h-[44px] rounded-xl border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-slate-100 sm:min-h-0">
          Dnes
        </button>
      </div>
      <div className="grid grid-cols-6 gap-1.5 sm:grid-cols-12">
        {ZKRATKY.map((zk, i) => {
          const iso = `${rok}-${String(i + 1).padStart(2, "0")}`;
          const je = iso === hodnota;
          const ted = iso === dnesniMesic;
          return (
            <button key={zk} type="button" onClick={() => onZmena(iso)}
              className={"min-h-[44px] rounded-xl border py-2 text-sm font-semibold transition-colors sm:min-h-0 " +
                (je ? "border-plum-600 bg-plum-600 text-white"
                    : ted ? "border-plum-300 bg-plum-50 text-plum-700 hover:bg-plum-100"
                          : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50")}>
              {zk}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function KlubJidelnicek() {
  // 1600 kcal je výchozí, je to nejčastější úroveň.
  const [kcal, setKcal] = useState(1600);
  const [mesic, setMesic] = useState(() => mesicIso(0));
  const [stav, setStav] = useState(null);      // { 1200: "zverejneno", ... }
  const [kopiruje, setKopiruje] = useState(false);
  const [obnov, setObnov] = useState(0);       // vynutí načtení stavu úrovní
  const m = makra(kcal);
  const klientId = `klub-jidelnicek-${kcal}-${mesic}`;

  // Přehled, které úrovně jsou pro zvolený měsíc hotové. Bez toho by se
  // muselo proklikat šest tlačítek, aby bylo vidět, co ještě chybí.
  useEffect(() => {
    let zij = true;
    apiFetch(`/api/jidelnicek?klub_prehled=${mesic}`)
      .then((r) => r.json())
      .then((j) => { if (zij && j?.ok) setStav(j.stav); })
      .catch(() => { if (zij) setStav(null); });
    return () => { zij = false; };
  }, [mesic, klientId, obnov]);

  async function rozkopirovat() {
    setKopiruje(true);
    try {
      const r = await apiFetch("/api/jidelnicek", {
        method: "POST",
        body: JSON.stringify({ action: "klub-rozkopirovat", klient_id: klientId }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Přepočet se nepovedl");
      const h = (j.hotove || []).length;
      const p = (j.preskocene || []).length;
      toast(h
        ? `Přepočítáno do ${h} úrovní${p ? `, ${p} přeskočeno (už něco mají)` : ""}.`
        : "Všechny ostatní úrovně už něco mají, nic jsem nepřepisoval.",
        h ? "success" : "info");
      setObnov((x) => x + 1);
    } catch (e) { toast(e.message, "error"); }
    finally { setKopiruje(false); }
  }

  return (
    <Stranka>
      <GradientHeader
        title="Jídelníček klubu"
        napoveda="Sdílené kalorické úrovně po měsících pro členky klub.fitway40.cz. Šest úrovní se liší jen velikostí porcí, ne recepty, proto stačí vygenerovat jednu a ostatní z ní přepočítat."
        icon={ChefHat}
      />

      <KartaPrehledu>
        <HlavickaDlazdice
          ikona={ChefHat}
          nazev={nazevMesice(mesic)}
          odznak={`${kcal} kcal`}
        />
        <div className="space-y-3 p-3 sm:p-4">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Měsíc</p>
            <VyberMesice hodnota={mesic} onZmena={setMesic} />
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Kalorická úroveň</p>
            <div className="flex flex-wrap gap-2">
              {UROVNE.map((k) => {
                const st = stav?.[k] || "nic";
                const je = k === kcal;
                return (
                  <button key={k} type="button" onClick={() => setKcal(k)}
                    title={`${k} kcal, ${STAV_POPIS[st]}`}
                    className={`${pill} ${je ? pillOn : STAV_STYL[st] + " hover:brightness-95"}`}>
                    {st === "zverejneno" ? <Check className="h-3.5 w-3.5" />
                      : st === "navrh" ? <Pencil className="h-3.5 w-3.5" /> : null}
                    {k} kcal
                  </button>
                );
              })}
            </div>
            <p className="mt-2 text-xs text-slate-500">
              Zelená je zveřejněná úroveň, oranžová rozdělaná, šedá zatím nepřipravená.
            </p>

            {/* Šest úrovní se liší jen velikostí porcí, ne recepty. Generovat
                je šestkrát AI je drahé a vyšlo by z toho šest různých
                jídelníčků; přepočet z jedné je levnější i konzistentnější. */}
            <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50/70 p-3">
              <p className="text-sm text-slate-600">
                <strong className="font-semibold text-slate-700">Stačí vygenerovat jednu úroveň.</strong>{" "}
                Ostatní z ní přepočítám: stejné recepty, jiné gramáže. Zelenina, koření a nápoje
                se nemění, kusové suroviny se zaokrouhlují na celé kusy. Vzniknou jako návrhy,
                zveřejňuješ je pořád ručně a úrovně, které už něco mají, přeskočím.
              </p>
              <button type="button" disabled={kopiruje} onClick={rozkopirovat}
                className="btn btn-primary mt-2.5">
                {kopiruje ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />}
                Rozkopírovat {kcal} kcal do ostatních úrovní
              </button>
            </div>
          </div>

          <p className="text-sm text-slate-500">
            Bílkoviny {m.bilkoviny} g, sacharidy {m.sacharidy} g, tuky {m.tuky} g.
            Bílkoviny drží 1,4 g na kilogram. Bez vnitřností, ryb a mořských plodů.
            Vždycky čtyři jídla: snídaně, svačina, oběd a večeře; svačina nemá pevný čas.
            Generátor dostane sezónní suroviny pro {nazevMesice(mesic).split(" ")[0]}.
          </p>
          <p className="text-sm text-slate-500">
            <strong className="font-semibold text-slate-700">Dotazník vyplňuješ ty, ne členka.</strong>{" "}
            Pro každou kombinaci měsíce a úrovně je připravený výchozí dotazník; rozbal si ho
            níž, uprav, co má být ten měsíc jinak, a pak teprve generuj.
          </p>
        </div>
      </KartaPrehledu>

      {/* key: přepnutí úrovně nebo měsíce musí editor nastartovat od nuly,
          jinak by v něm zůstal rozpracovaný koncept z předchozí kombinace. */}
      <JidelnicekSection
        key={klientId}
        klientId={klientId}
        klientName={`Klub ${kcal} kcal, ${nazevMesice(mesic)}`}
      />
    </Stranka>
  );
}
